import { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import { clearAllStudentDrafts } from "../../../utils/studentDraftStorage";

const StudentAuthContext = createContext();

export const StudentAuthProvider = ({ children }) => {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state
  const apiUrl = process.env.REACT_APP_API_URL;
  const persistStudent = (studentData) => {
    localStorage.setItem("student", JSON.stringify(studentData));
    localStorage.setItem("accessToken", studentData.token);
    localStorage.setItem("refreshToken", studentData.refreshToken);
  };
  const studentLogin = (studentData) => {
    const fullUser = {
      token: studentData.token,
      refreshToken: studentData.refresh_token,
      student: studentData.student, // keep it as nested object
    };

    setStudent(fullUser);
    persistStudent(fullUser);
  };
  const studentLogout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      // console.error("Logout error:", error.message);
    }

    clearStoredData();
    clearAllStudentDrafts();
    toast.success("Logged out successfully");
    window.location.href = "/login"; // redirect to login
  };
  const studentSessionLogout = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      if (accessToken) {
        await fetch(`${apiUrl}/auth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      // console.error("Logout error:", error.message);
    }

    clearStoredData();
    clearAllStudentDrafts();
    window.location.href = "/session-expired"; // redirect to login
  };
  const clearStoredData = () => {
    setStudent(null);

    ["student", "accessToken", "refreshToken", "rememberMe"].forEach((key) =>
      localStorage.removeItem(key),
    );
  };
  const refreshAccessToken = async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      // console.warn("❌ No refresh token found. Logging out.");
      studentSessionLogout();
      return null;
    }
    try {
      const response = await fetch(`${apiUrl}/auth/refresh-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) throw new Error("Refresh token invalid");

      const data = await response.json();
      const updatedUser = {
        student: data?.student, // always keep the existing user object
        token: data.access_token,
        refreshToken: refreshToken,
      };
      setStudent(updatedUser);
      persistStudent(updatedUser);
      // persistUser(refreshedUser);
      return data.access_token;
    } catch (error) {
      studentSessionLogout();
    }
  };
  const fetchWithAuthStudent = async (url, options = {}) => {
    let accessToken = localStorage.getItem("accessToken");

    const isFormData = options.body instanceof FormData;

    let headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${accessToken}`,
    };

    // Only set JSON content type if NOT using FormData
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      const newToken = await refreshAccessToken();
      if (!newToken) {
        return null;
      }

      headers.Authorization = `Bearer ${newToken}`;

      response = await fetch(url, {
        ...options,
        headers,
      });
    }

    return response;
  };

  // 🔄 Load user on mount
  useEffect(() => {
    const tryRestoreSession = async () => {
      const storedUser = JSON.parse(localStorage.getItem("student"));
      const refreshToken = storedUser?.refreshToken;
      if (storedUser && refreshToken) {
        try {
          const response = await fetch(`${apiUrl}/auth/refresh-token`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          const data = await response.json();

          // Only clear storage if refresh token is truly expired/invalid
          if (
            response.status === 401 ||
            data.error === "invalid_grant" ||
            data.message === "Refresh token expired" ||
            data.resp_code === "session_expired"
          ) {
            throw new Error("Refresh token invalid or expired");
          }
          if (data.access_token || data.token) {
            const updatedUser = {
              token: data.access_token || data.token,
              refreshToken: refreshToken,
              student: data.student || storedUser.student,
            };
            setStudent(updatedUser);
            persistStudent(updatedUser);
          } else {
            setStudent(storedUser);
          }
        } catch (err) {
          clearStoredData();
          window.location.href = "/session-expired";
        }
      } else {
        setStudent(null);
      }
      setLoading(false);
    };

    tryRestoreSession();
  }, [apiUrl]);

  return (
    <StudentAuthContext.Provider
      value={{
        student,
        studentLogin,
        setStudent,
        studentLogout,
        fetchWithAuthStudent,
        loading,
        clearStoredData,
      }}
    >
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useAuthStudent = () => useContext(StudentAuthContext);
